import { parse } from 'csv-parse/sync';

import { CsvIssue, OrderGuideUploadRecord } from '../types';

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

/**
 * Detects the field delimiter from the header line by counting candidates
 * outside quoted sections. Excel in many European locales exports with `;`.
 */
export const detectDelimiter = (headerLine: string): ',' | ';' => {
  let commas = 0;
  let semicolons = 0;
  let inQuotes = false;
  headerLine.split('').forEach((char) => {
    if (char === '"') inQuotes = !inQuotes;
    else if (!inQuotes && char === ',') commas += 1;
    else if (!inQuotes && char === ';') semicolons += 1;
  });
  return semicolons > commas ? ';' : ',';
};

/**
 * RFC-4180 CSV parsing (quoted fields, escaped quotes, embedded newlines,
 * LF/CRLF) is delegated to `csv-parse`; this keeps only the parts specific
 * to this template — delimiter auto-detection and dropping blank rows.
 * Throws on an unterminated quoted field.
 */
export const parseCsv = (input: string): ParsedCsv => {
  // Strip UTF-8 BOM
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const firstLineEnd = text.search(/\r\n|\r|\n/);
  const delimiter = detectDelimiter(
    firstLineEnd === -1 ? text : text.slice(0, firstLineEnd)
  );

  let rows: string[][];
  try {
    rows = parse(text, {
      delimiter,
      // Rows shorter/longer than the header are common (trailing columns
      // left blank) — recordsFromCsv already treats a missing cell as ''.
      relax_column_count: true,
      skip_empty_lines: false,
    });
  } catch {
    throw new Error('Unterminated quoted field — the CSV file is malformed.');
  }

  const nonEmpty = rows.filter((cells) =>
    cells.some((cell) => cell.trim() !== '')
  );
  if (nonEmpty.length === 0) {
    return { headers: [], rows: [] };
  }
  const [headers, ...dataRows] = nonEmpty;
  return { headers: headers.map((header) => header.trim()), rows: dataRows };
};

type RecordField = Exclude<keyof OrderGuideUploadRecord, 'rowNumber'>;

/**
 * Accepted header spellings, lowercased with spaces/underscores removed.
 * `chatids` keeps compatibility with the legacy Retool template.
 */
const HEADER_ALIASES: Record<string, RecordField> = {
  chatid: 'chatId',
  chatids: 'chatId',
  orderguideid: 'orderGuideId',
  orderguideids: 'orderGuideId',
  category: 'category',
  productid: 'productId',
  productname: 'productName',
  unit: 'unit',
  par: 'par',
  currency: 'currency',
  price: 'price',
  displayname: 'displayName',
};

const REQUIRED_COLUMNS: { field: RecordField; label: string }[] = [
  { field: 'chatId', label: 'chat_id' },
  { field: 'productName', label: 'product_name' },
  { field: 'unit', label: 'unit' },
];

const normalizeHeader = (header: string): string =>
  header.toLowerCase().replace(/[\s_-]/g, '');

export interface CsvRecordsResult {
  records: OrderGuideUploadRecord[];
  issues: CsvIssue[];
}

/**
 * Maps parsed CSV rows onto named records using the template headers.
 * Unknown columns are ignored with a warning; missing required columns
 * are a file-level error.
 */
export const recordsFromCsv = (parsed: ParsedCsv): CsvRecordsResult => {
  const issues: CsvIssue[] = [];
  const columnFields: (RecordField | null)[] = parsed.headers.map((header) => {
    const field = HEADER_ALIASES[normalizeHeader(header)];
    if (!field) {
      issues.push({
        row: 1,
        column: header,
        message: `Unknown column "${header}" was ignored.`,
        severity: 'warning',
      });
      return null;
    }
    return field;
  });

  REQUIRED_COLUMNS.forEach(({ field, label }) => {
    if (!columnFields.includes(field)) {
      issues.push({
        row: 1,
        column: label,
        message: `Missing required column "${label}".`,
        severity: 'error',
      });
    }
  });
  if (issues.some((issue) => issue.severity === 'error')) {
    return { records: [], issues };
  }

  const records = parsed.rows.map((cells, index) => {
    const record: OrderGuideUploadRecord = {
      rowNumber: index + 2,
      chatId: '',
      orderGuideId: '',
      category: '',
      productId: '',
      productName: '',
      unit: '',
      par: '',
      currency: '',
      price: '',
      displayName: '',
    };
    columnFields.forEach((field, columnIndex) => {
      if (field) {
        const value = (cells[columnIndex] ?? '').trim();
        // CSV currency codes are hand-typed; normalize case so "eur"/"usd"
        // match the uppercase ISO-4217 codes the validators check against.
        record[field] = field === 'currency' ? value.toUpperCase() : value;
      }
    });
    return record;
  });

  return { records, issues };
};
